import { Box, Typography } from "@mui/material";
import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { SystemTabs } from "../../components/UIToolkit/Clickable/TabsAndChips/SystemTabs/SystemTabs";
import type { SystemTab } from "../../components/UIToolkit/Clickable/TabsAndChips/SystemTabs/utils";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../contexts/ChosenDepartment";
import { INK, NEUTRAL } from "../../theme/tokens";
import { CHANGELOG_PARAM, SHIFTS_TAB_PARAM_VALUE } from "../../utils/shiftChangelogLink";
import { EmployeeFieldChangesView } from "./EmployeeFieldChangesView";
import { ShiftLineageView } from "./ShiftLineageView";

type ChangelogTab = "details" | "shifts";

export const EmployeeChangelog: FC = () => {
	const { t } = useTranslation();
	const { chosenDepartment } = useChosenDepartment();
	const { track } = useAnalytics();
	// A link from a shift's raw punches opens straight on Shifts & Punches. Read ONCE as the initial tab
	// (not on every render) so the reader can switch tabs afterwards without the URL snapping them back.
	const [searchParams] = useSearchParams();
	const [tab, setTab] = useState<ChangelogTab>(searchParams.get(CHANGELOG_PARAM.tab) === SHIFTS_TAB_PARAM_VALUE ? "shifts" : "details");

	const tabs: SystemTab<ChangelogTab>[] = [
		{ label: t("employeeChangelog.tab.details"), value: "details" },
		{ label: t("employeeChangelog.tab.shiftsAndPunches"), value: "shifts" },
	];

	const handleTabChange = (next: ChangelogTab) => {
		setTab(next);
		track("employee_changelog_tab_changed", { department_id: chosenDepartment?.department_id, tab: next });
	};

	return (
		// The page shell lays its children out in a ROW, so without an explicit width this page shrinks to
		// its widest control (~830px) and the table is crammed into two thirds of the window.
		<Box sx={{ height: "100%", width: "100%", minWidth: 0, display: "flex", flexDirection: "column", gap: 2, p: 3 }}>
			<Typography sx={{ color: INK.SECONDARY, fontSize: "1rem", fontWeight: "bold" }}>{t("employeeChangelog.title")}</Typography>
			<SystemTabs<ChangelogTab>
				tabs={tabs}
				currentTab={tab}
				onTabChange={handleTabChange}
				dataTestId="employee-changelog-tabs"
				sx={{ borderBottom: `1px solid ${NEUTRAL[200]}` }}
			/>
			<Box sx={{ flex: 1, minHeight: 0, minWidth: 0 }}>{tab === "details" ? <EmployeeFieldChangesView /> : <ShiftLineageView />}</Box>
		</Box>
	);
};
